/**
 * useDemoMode Hook
 *
 * Manages demo mode state and summary polling for interview sessions.
 * Demo mode shows a post-call summary to the user for demonstration purposes.
 *
 * @module hooks/useDemoMode
 */

import { useCallback, useEffect, useState } from 'react';
import type { UserProfile } from '@/types/interview';
import { logger } from '@/utils/logger';
import { createClient } from '@/utils/supabase/client';

// localStorage key for demo mode fallback (dev only)
const DEMO_MODE_KEY = 'dd.demo';

// Re-export for backwards compatibility
export type { UserProfile } from '@/types/interview';

/** Options for the useDemoMode hook. */
export interface UseDemoModeOptions {
  /** User profile with optional demo_mode flag */
  profile: UserProfile | null;
  /** Current session ID to poll summary for */
  sessionId: string | null;
  /** Whether the interview has completed (triggers summary polling) */
  isComplete: boolean;
  /** Summary supplied directly by the caller (e.g. from updateSessionOnCallEnd).
   *  When provided, polling is skipped entirely. */
  initialSummary?: string | null;
}

export interface UseDemoModeReturn {
  /** Whether demo mode is enabled */
  demoMode: boolean;
  /** The demo summary text (null if not yet loaded) */
  demoSummary: string | null;
  /** Whether summary is currently being fetched */
  loadingSummary: boolean;
  /** Reset demo state for a new interview */
  resetDemoState: () => void;
}

/**
 * Hook for managing demo mode state and summary polling
 *
 * Demo mode is resolved in order of priority:
 * 1. Profile demo_mode flag (from database)
 * 2. localStorage fallback (dev only)
 *
 * When an interview completes in demo mode, the hook polls for
 * the summary with retry logic until it becomes available.
 */
export function useDemoMode(options: UseDemoModeOptions): UseDemoModeReturn {
  const { profile, sessionId, isComplete, initialSummary } = options;

  const [demoMode, setDemoMode] = useState(false);
  const [demoSummary, setDemoSummary] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Resolve demo mode from profile or localStorage
  useEffect(() => {
    try {
      // Priority 1: Profile flag
      if (profile && typeof profile.demo_mode === 'boolean') {
        const on = !!profile.demo_mode;
        logger.debug('Demo mode resolved from profile', {
          on,
          profileId: profile.id,
        });
        setDemoMode(on);
        return;
      }

      // Priority 2: localStorage fallback (dev only)
      if (typeof window !== 'undefined') {
        const persisted = window.localStorage.getItem(DEMO_MODE_KEY);
        const on = persisted === '1';
        logger.debug('Demo mode resolved from localStorage (fallback)', { on });
        setDemoMode(on);
      }
    } catch {
      // localStorage not available or other error - default to false
      setDemoMode(false);
    }
  }, [profile]);

  // Accept summary supplied by caller (bypasses polling entirely)
  useEffect(() => {
    if (demoMode && initialSummary && isComplete && !demoSummary) {
      logger.debug('Demo summary received from caller', {
        sessionId,
        summaryLength: initialSummary.length,
      });
      setDemoSummary(initialSummary);
      setLoadingSummary(false);
    }
  }, [demoMode, initialSummary, isComplete, demoSummary, sessionId]);

  // Load demo summary when interview completes (fallback if caller didn't supply one)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omit demoSummary to avoid re-fetch loop
  useEffect(() => {
    if (!demoMode || !isComplete || !sessionId || demoSummary || initialSummary)
      return;

    const loadDemoSummary = async () => {
      logger.debug('Demo summary effect triggered', {
        demoMode,
        isComplete,
        sessionId,
      });

      setLoadingSummary(true);

      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('interview_sessions')
          .select('summary,status')
          .eq('id', sessionId)
          .single();

        logger.debug('Demo summary fetch result', {
          hasError: !!error,
          errorMsg: error?.message,
          status: data?.status,
          hasSummary: !!data?.summary,
        });

        if (!error && data?.summary) {
          setDemoSummary(prev => prev ?? data.summary);
          setLoadingSummary(false);
          return;
        }
      } catch {
        // Initial fetch failed, will retry in polling effect
      }

      setLoadingSummary(false);
    };

    void loadDemoSummary();
  }, [demoMode, isComplete, sessionId, initialSummary]);

  // Late-poll for summary if not yet available
  useEffect(() => {
    if (!demoMode || !isComplete || !sessionId || demoSummary || initialSummary)
      return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const pollIntervalMs = 1000;
    let currentTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const supabase = createClient();

    const tick = async () => {
      if (cancelled) return;

      attempts += 1;

      try {
        const { data } = await supabase
          .from('interview_sessions')
          .select('summary,status')
          .eq('id', sessionId)
          .single();

        logger.debug('Demo summary late-poll tick', {
          attempts,
          hasSummary: !!data?.summary,
          status: data?.status,
        });

        if (data?.summary) {
          setDemoSummary(prev => prev ?? data.summary);
          return;
        }
      } catch {
        // Polling error, will retry
      }

      // Schedule next tick only if not cancelled
      if (!cancelled && attempts < maxAttempts) {
        currentTimeoutId = setTimeout(tick, pollIntervalMs);
      }
    };

    // Start polling after a short delay
    currentTimeoutId = setTimeout(tick, 800);

    return () => {
      cancelled = true;
      if (currentTimeoutId) {
        clearTimeout(currentTimeoutId);
      }
    };
  }, [demoMode, isComplete, sessionId, demoSummary, initialSummary]);

  // Reset demo state for a new interview
  const resetDemoState = useCallback(() => {
    setDemoSummary(null);
    setLoadingSummary(false);
  }, []);

  return {
    demoMode,
    demoSummary,
    loadingSummary,
    resetDemoState,
  };
}

export default useDemoMode;
